import { useEffect, useState, useContext, useCallback } from "react";
import { SALE_STATUS, ESTIMATE_STATUS_LABELS, INVOICE_FORMATS } from "const";
import DataTable from "components/utils/DataTable";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import ShopContext from "context/ShopContext";
import styles from "./styles.module.css";
import EmptyState from "components/utils/EmptyState/EmptyState";
import toast from "react-hot-toast";
import PrinterImg from "images/svg/printer.svg";
import * as ReportHelper from "helpers/ReportHelper";
import * as SalesHelper from "helpers/SalesHelper";
import * as StringHelper from "helpers/StringHelper";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import SyncIcon from "images/svg/refresh-2.svg";
import ShopsHelper from "helpers/ShopsHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import EstimateDetailsDialog from "components/popups/EstimateDetailsDialog/EstimateDetailsDialog";
import * as EstimateHelper from "helpers/EstimateHelper";

export default function EstimateBillSummaryReport({ shiftId }) {
  const { shop } = useContext(ShopContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEstimateId, setSelectedEstimateId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSyncData = async () => {
    try {
      setSyncing(true);
      await EstimateHelper.syncPendingEstimates();
      toast.success("Sync completed");
      await fetchReport();
    } catch (error) {
      toast.error("Failed to sync estimates");
    } finally {
      setSyncing(false);
    }
  };

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const estimateSales = await SalesHelper.getEstimatesByShift(shiftId);
      setRows(estimateSales);
    } catch (error) {
      toast.error("Fetch report failed");
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const headerData = [
    {
      label: "Estimate No",
      id: "estimate_no",
      type: "text",
    },
    {
      label: "Date",
      id: "date",
      type: "callback",
      width: "110px",
      viewRender: (item) => DateTimeHelpers.getDate(item.time),
    },
    {
      label: "Time",
      id: "time",
      type: "callback",
      width: "100px",
      viewRender: (item) => DateTimeHelpers.getLocalTime(item.time),
    },
    {
      label: "Customer",
      id: "customer_name",
      type: "callback",
      viewRender: (item) => item?.customer?.name || "-",
    },
    {
      label: "Mobile",
      id: "customer_mobile",
      type: "callback",
      viewRender: (item) => item?.customer?.mobile || "-",
    },
    {
      label: "Discount",
      id: "discount_applied_on_items",
      type: "floatAmount",
    },
    {
      label: "Net Amount",
      id: "total",
      type: "floatAmount",
    },
    {
      label: "Tax Amount",
      id: "tax",
      type: "floatAmount",
    },
    {
      label: "Additional Tax",
      id: "cess",
      type: "floatAmount",
    },
    {
      label: "Total Amount",
      id: "payable",
      type: "floatAmount",
    },
    {
      label: "Status",
      id: "status",
      type: "callback",
      viewRender: (item) =>
        item.status === SALE_STATUS.PAID ? ESTIMATE_STATUS_LABELS.CONVERTED : StringHelper.formatLabel(item.status),
    },
  ];

  const handlePrintSalesReport = async () => {
    const printRows = rows.map((r) => ({ ...r, bill_no: String(r.estimate_no) }));

    const terminalSettings = TerminalHelper.getTerminalSettings();
    const invoiceFormat = terminalSettings.invoicePrinterFormat || INVOICE_FORMATS.THREE_INCH;

    let reportText;
    if (
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE_ARABIC ||
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE
    ) {
      reportText = await ReportHelper.getSalesReportMultiThreeInch(printRows, shop, shiftId, []);
    } else {
      reportText = await ReportHelper.getSalesReportThreeInch(printRows, shop, shiftId, []);
    }

    await ReportHelper.printReport({ reportText, fallbackFunction: window.print });
  };

  const _getSalesReportSummary = (items) => {
    let totalPayable = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;
    let totalDiscount = 0;
    let totalNetAmount = 0;

    items.forEach((item) => {
      totalPayable += item?.payable || 0;
      totalTax += item.tax || 0;
      totalAdditionalTax += item.cess || 0;
      totalDiscount += item.discount_applied_on_items || 0;
      totalNetAmount += item.total || 0;
    });

    return {
      totalPayable: ShopsHelper.getAmountFormatted(shop, totalPayable),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
      totalDiscount: ShopsHelper.getAmountFormatted(shop, totalDiscount),
      totalNetAmount: ShopsHelper.getAmountFormatted(shop, totalNetAmount),
    };
  };

  const summary = _getSalesReportSummary(rows);

  return (
    <div className={styles.container}>
      <div style={{ height: "100%" }}>
        <div className={styles.headerSection}>
          <div>
            <h2 className={styles.title}>Estimate Bill Summary</h2>
            <h3 className={styles.subTitle}>Key financials of the estimates for current shift</h3>
          </div>

          <div className={styles.btnSection}>
            <ButtonRed
              onClick={handlePrintSalesReport}
              startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
            >
              Print Report
            </ButtonRed>
            <div className={styles.refreshButton} onClick={fetchReport}>
              <img src={SyncIcon} alt="Refresh" />
              Refresh
            </div>
            <div
              className={styles.refreshButton}
              onClick={syncing ? undefined : handleSyncData}
              style={{ opacity: syncing ? 0.5 : 1, cursor: syncing ? "wait" : "pointer" }}
            >
              <img src={SyncIcon} alt="Sync" />
              {syncing ? "Syncing..." : "Sync Data"}
            </div>
          </div>
        </div>

        {loading ? (
          <SkeletonLoading isOpen={true} count={8} height={45} xs={12} />
        ) : rows.length > 0 ? (
          <>
            <DataTable
              columns={headerData}
              rows={rows}
              rowKey="local_id"
              rowClickListener={(row) => {
                setSelectedEstimateId(row.local_id);
                setIsDialogOpen(true);
              }}
              summary={{
                estimate_no: "Total",
                tax: `${shop?.currency || ""} ${summary.totalTax}`,
                cess: `${shop?.currency || ""} ${summary.totalAdditionalTax}`,
                discount_applied_on_items: `${shop?.currency || ""} ${summary.totalDiscount}`,
                total: `${shop?.currency || ""} ${summary.totalNetAmount}`,
                payable: `${shop?.currency || ""} ${summary.totalPayable}`,
              }}
            />
            {isDialogOpen && (
              <EstimateDetailsDialog
                open={isDialogOpen}
                estimateId={selectedEstimateId}
                onClose={() => setIsDialogOpen(false)}
              />
            )}
          </>
        ) : (
          <EmptyState title="Estimate Bill Summary" />
        )}
      </div>
    </div>
  );
}
